import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Scroll from 'react-scroll';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faImage, faTachometer, faEye, faPencil, faTrashCan, faPlus, faTable, faArrowRight, faRefresh } from '@fortawesome/free-solid-svg-icons';
import { getCMSImageListAPI, deleteCMSImageAPI } from "../../API/CMSImage";
import { useRecoilState } from 'recoil';

import FormErrorBox from "../Reusable/FormErrorBox";
import PageLoadingContent from "../Reusable/PageLoadingContent";
import { topAlertMessageState, topAlertStatusState } from "../../AppState";


function ImageList() {

    ////
    //// Global state.
    ////

    const [topAlertMessage, setTopAlertMessage] = useRecoilState(topAlertMessageState);
    const [topAlertStatus, setTopAlertStatus] = useRecoilState(topAlertStatusState);

    ////
    //// Component states.
    ////

    const [errors, setErrors] = useState({});
    const [images, setImages] = useState("");
    const [selectedImageForDeletion, setSelectedImageForDeletion] = useState("");
    const [isFetching, setFetching] = useState(false);

    ////
    //// API.
    ////

    function onImageListSuccess(response){
        console.log("onImageListSuccess: Starting...");
        setImages(response);
    }

    function onImageListError(apiErr) {
        console.log("onImageListError: Starting...");
        setErrors(apiErr);

        // The following code will cause the screen to scroll to the top of
        // the page. Please see ``react-scroll`` for more information:
        // https://github.com/fisshy/react-scroll
        var scroll = Scroll.animateScroll;
        scroll.scrollToTop();
    }

    function onImageListDone() {
        console.log("onImageListDone: Starting...");
        setFetching(false);
    }

    function onImageDeleteSuccess(response){
        console.log("onImageDeleteSuccess: Starting..."); // For debugging purposes only.

        // Update notification.
        setTopAlertStatus("success");
        setTopAlertMessage("Image deleted");
        setTimeout(() => {
            console.log("onDeleteConfirmButtonClick: topAlertMessage, topAlertStatus:", topAlertMessage, topAlertStatus);
            setTopAlertMessage("");
        }, 2000);

        // Fetch again an updated list.
        fetchList();
    }

    function onImageDeleteError(apiErr) {
        console.log("onImageDeleteError: Starting..."); // For debugging purposes only.
        setErrors(apiErr);

        // Update notification.
        setTopAlertStatus("danger");
        setTopAlertMessage("Failed deleting");
        setTimeout(() => {
            console.log("onImageDeleteError: topAlertMessage, topAlertStatus:", topAlertMessage, topAlertStatus);
            setTopAlertMessage("");
        }, 2000);

        // The following code will cause the screen to scroll to the top of
        // the page. Please see ``react-scroll`` for more information:
        // https://github.com/fisshy/react-scroll
        var scroll = Scroll.animateScroll;
        scroll.scrollToTop();
    }

    function onImageDeleteDone() {
        console.log("onImageDeleteDone: Starting...");
        setFetching(false);
    }

    ////
    //// Event handling.
    ////

    const fetchList = () => {
        setFetching(true);
        setErrors({});
        getCMSImageListAPI(
            onImageListSuccess,
            onImageListError,
            onImageListDone
        );
    }

    const onSelectImageForDeletion = (e, image) => {
        console.log("onSelectImageForDeletion", image);
        setSelectedImageForDeletion(image);
    }

    const onDeselectImageForDeletion = (e) => {
        console.log("onDeselectImageForDeletion");
        setSelectedImageForDeletion("");
    }

    const onDeleteConfirmButtonClick = (e) => {
        console.log("onDeleteConfirmButtonClick"); // For debugging purposes only.

        deleteCMSImageAPI(
            selectedImageForDeletion.id,
            onImageDeleteSuccess,
            onImageDeleteError,
            onImageDeleteDone
        );
        setSelectedImageForDeletion("");

    }

    ////
    //// Misc.
    ////

    useEffect(() => {
        let mounted = true;

        if (mounted) {
            window.scrollTo(0, 0);  // Start the page at the top of the page.
            fetchList();
        }

        return () => { mounted = false; }
    }, []);

    ////
    //// Component rendering.
    ////

    return (
        <>
            <div class="container">
                <section class="section">
                    {/* Desktop Breadcrumbs */}
                    <nav class="breadcrumb is-hidden-touch" aria-label="breadcrumbs">
                        <ul>
                            <li><Link to="/dashboard"><FontAwesomeIcon className="fas" icon={faTachometer} />&nbsp;Dashboard</Link></li>
                            <li class="is-active"><Link to="/cms-images" aria-current="page"><FontAwesomeIcon className="fas" icon={faImage} />&nbsp;Images</Link></li>
                        </ul>
                    </nav>

                    {/* Mobile Breadcrumbs */}
                    <nav class="breadcrumb is-hidden-desktop" aria-label="breadcrumbs">
                        <ul>
                            <li class=""><Link to={`/dashboard`} aria-current="page"><FontAwesomeIcon className="fas" icon={faArrowLeft} />&nbsp;Back to Dashboard</Link></li>
                        </ul>
                    </nav>

                    {/* Page */}
                    <nav class="box">
                        <div class={`modal ${selectedImageForDeletion ? 'is-active' : ''}`}>
                            <div class="modal-background"></div>
                            <div class="modal-card">
                                <header class="modal-card-head">
                                    <p class="modal-card-title">Are you sure?</p>
                                    <button class="delete" aria-label="close" onClick={onDeselectImageForDeletion}></button>
                                </header>
                                <section class="modal-card-body">
                                    You are about to <strong>permanently delete</strong> this image and all the data inside of it from all sources. This action is cannot be undone. Are you sure you would like to continue?
                                </section>
                                <footer class="modal-card-foot">
                                    <button class="button is-success" onClick={onDeleteConfirmButtonClick}>Confirm</button>
                                    <button class="button" onClick={onDeselectImageForDeletion}>Cancel</button>
                                </footer>
                            </div>
                        </div>

                        <div class="columns">
                            <div class="column">
                                <h1 class="title is-4"><FontAwesomeIcon className="fas" icon={faImage} />&nbsp;Images</h1>
                            </div>
                            {!isFetching &&<div class="column has-text-right">
                                {/* Mobile Specific */}
                                <button class="button is-small is-info is-fullwidth is-hidden-desktop" onClick={fetchList}>
                                    <FontAwesomeIcon className="mdi" icon={faRefresh} />
                                </button>&nbsp;
                                <Link to={`/cms-images/add`} class="button is-small is-success is-fullwidth is-hidden-desktop" type="button">
                                    <FontAwesomeIcon className="mdi" icon={faPlus} />&nbsp;Add
                                </Link>
                                {/* Desktop Specific */}
                                <button class="button is-small is-info is-hidden-touch" onClick={fetchList}>
                                    <FontAwesomeIcon className="mdi" icon={faRefresh} />
                                </button>&nbsp;
                                <Link to={`/cms-images/add`} class="button is-small is-success is-hidden-touch" type="button">
                                    <FontAwesomeIcon className="mdi" icon={faPlus} />&nbsp;Add
                                </Link>
                            </div>}
                        </div>
                        <FormErrorBox errors={errors} />

                        {isFetching
                            ? <>
                                <PageLoadingContent displayMessage={"Please wait"} />
                            </>
                            : <>
                                {images && images.length > 0
                                    ?
                                    <div class="container">
                                        <div class="columns is-multiline">
                                        {images && images.map(function(image, i){
                                            return <div class="column is-one-third" key={image.id}>
                                                <div class="card">
                                                    <div class="card-image p-3">
                                                        <figure class="image is-4by3">
                                                            <img src={image.publicObjectUrl} alt="Placeholder image" />
                                                        </figure>
                                                    </div>
                                                    <header class="card-header">
                                                        <p class="card-header-title">
                                                            {image.name}
                                                        </p>
                                                        <button class="card-header-icon" aria-label="more options">
                                                          <span class="icon">
                                                            <i class="fas fa-angle-down" aria-hidden="true"></i>
                                                          </span>
                                                        </button>
                                                    </header>
                                                    <div class="card-content">
                                                        <div class="content">
                                                            {image.description}
                                                        </div>
                                                    </div>
                                                    <footer class="card-footer">
                                                        <Link to={`/cms-image/${image.id}`} class="card-footer-item"><FontAwesomeIcon className="mdi" icon={faEye} />&nbsp;View</Link>
                                                        {/*
                                                        <Link to={`/cms-image/${image.id}/edit`} class="card-footer-item"><FontAwesomeIcon className="mdi" icon={faPencil} />&nbsp;Edit</Link>
                                                        <Link onClick={(e, ses) => onSelectImageForDeletion(e, image)} class="card-footer-item"><FontAwesomeIcon className="mdi" icon={faTrashCan} />&nbsp;Delete</Link>
                                                        */}
                                                    </footer>
                                                </div>
                                            </div>;
                                            })}
                                        </div>
                                    </div>
                                    :
                                    <section class="hero is-medium has-background-white-ter">
                                      <div class="hero-body">
                                        <p class="title">
                                            <FontAwesomeIcon className="fas" icon={faTable} />&nbsp;No Images
                                        </p>
                                        <p class="subtitle">
                                            No images. <b><Link to="/cms-images/add">Click here&nbsp;<FontAwesomeIcon className="mdi" icon={faArrowRight} /></Link></b> to get started uploading your first image.
                                        </p>
                                      </div>
                                    </section>
                                }
                            </>
                        }
                    </nav>
                </section>
            </div>
        </>
    );
}

export default ImageList;
